import db from '@/database'
import * as Sentry from '@sentry/vue'
import inMemoryCacheFactory from '@/script/cache/utils'
import { checkSentry } from '@/script'
import { GetUserDataResult, SignInResult, SignUpSettings } from '@/types/auth'
import { clearAuthStorage, getValidToken, setItemWithExpiry, STORAGE_KEYS } from '@/script/storageToken'
import { config } from '@/script/cache/configuration'
import { apiList, $axios, $axiosUrl } from '@/api'

export default {
    state: () => ({
        user: null,
        isAdmin: false,
        userToken: null,
        signUpSettings: false,
        //eslint-disable-next-line
        emailRegex: /^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
    }),

    mutations: {
        setUserToken(state, payload) {
            state.userToken = payload
        },
        setUserIsAdmin(state, payload) {
            state.isAdmin = payload
        },
        setUser(state, payload) {
            if (checkSentry()) {
                Sentry.setUser(payload)
            }

            state.user = payload
            localStorage.setItem('lang', `"${payload.lang}"`)

            if (payload?.preferences?.auth_forcepasswordchange && !window.location.pathname.startsWith('/profile')) {
                window.location.pathname = '/profile'
            }
        },
        setSignUpSetting(state, payload) {
            state.signUpSettings = payload
        }
    },

    actions: {
        //eslint-disable-next-line
        /**
         *
         * @param dispatch
         * @param commit
         * @param payload {SignUpUserParams}
         * @return {Promise<SignUpUserResult>}
         */
        signUpUser({ dispatch, commit }, payload) {
            return new Promise((resolve, reject) => {
                payload = { ...payload, version: 2 } // Inject new version of web_api handler
                $axios({
                    method: apiList.auth.signUp.method,
                    params: {
                        wsfunction: apiList.auth.signUp.moodle_method
                    },
                    data: payload
                })
                    .then(resp => {
                        if (!resp.data.errorcode) {
                            resolve(resp.data)
                        } else {
                            reject(resp.data)
                        }
                    }).catch(err => reject(err))
            })
        },

        /**
         * @param commit
         * @return {Promise<SignUpSettings>}
         */
        getSignUpSettings({ commit }) {
            return new Promise((resolve, reject) => {
                $axios({
                    method: apiList.auth.signUpSettings.method,
                    params: {
                        wsfunction: apiList.auth.signUpSettings.moodle_method
                    }
                }).then(resp => {
                    commit('setSignUpSetting', new SignUpSettings(resp.data))
                    resolve(new SignUpSettings(resp.data))
                }).catch(err => {
                    reject(err)
                })
            })
        },

        /**
         * @description Login action
         * @param {SingInParams} payload Test user login - student_tester password - qweQwe1@
         */
        async logInUser({ dispatch, commit }, payload) {
            try {
                /**
                 * @type {SignInResult}
                 */
                const result = await dispatch('getUserToken', payload)

                setItemWithExpiry(STORAGE_KEYS.AUTH_TOKEN, result.token)
                localStorage.setItem(STORAGE_KEYS.USER_EMAIL, payload.email)

                const result2 = await dispatch('getUserData', { email: payload.email })
                Sentry.setUser({
                    id: result2.id,
                    email: result2.email,
                    username: result2.username,
                    fullname: result2.fullname,
                    geo: {
                        country_code: result2.country,
                        city: result2.city
                    }
                })
                return Promise.resolve(result2)
            } catch (err) {
                return Promise.reject(err)
            }
        },

        /**
         *
         * @description Check is current user has Admin role
         *
         * @returns Promise
         */
        async checkUserIsAdmin({ commit }) {
            try {
                const user = await $axios({
                    method: apiList.user.checkUserIsAdmin.method,
                    params: {
                        wsfunction: apiList.user.checkUserIsAdmin.moodle_method
                    }
                }).then(resp => resp.data)
                commit('setUserIsAdmin', user.isAdmin)
                return Promise.resolve(user.isAdmin)
            } catch (err) {
                commit('setUserIsAdmin', false)
                return Promise.reject(err)
            }
        },
        /**
         *
         * @description Check is current user has Admin role
         *
         * @returns Promise
         */
        changeUserPassword(_, { password, newPassword }) {
            const data = new FormData()
            data.append('old_password', password)
            data.append('new_password', newPassword)

            return $axios({
                method: apiList.auth.changeUserPassword.method,
                params: {
                    wsfunction: apiList.auth.changeUserPassword.moodle_method
                },
                data
            })
        },

        LoginInAUser({ dispatch, commit }, payload) {
            return $axios({
                method: apiList.auth.loginInAUser.method,
                params: {
                    wsfunction: apiList.auth.loginInAUser.moodle_method
                },
                data: {
                    id: payload.courseId ?? 1,
                    user: payload.userId,
                    serviceshortname: config.service
                }
            })
                .then(response => {
                    if (response.data.errorcode || !response.data) {
                        return Promise.reject(response.data || response)
                    }

                    response.data.email = payload.email

                    return dispatch('setUserTokenInAUser', response.data)
                })
                .catch(err => {
                    return Promise.reject(err)
                })

        },

        setUserTokenInAUser({ dispatch, commit }, userInfo) {
            return new Promise(async (resolve, reject) => {
                try {
                    await db.clearCache()

                    setItemWithExpiry(STORAGE_KEYS.AUTH_TOKEN, userInfo.userToken)
                    localStorage.setItem(STORAGE_KEYS.USER_EMAIL, userInfo.email)
                    commit('setUserToken', { token: userInfo.userToken })

                    const userData = await dispatch('getUserData', { email: userInfo.email })
                    await dispatch('checkUserIsAdmin')

                    resolve(userData)
                } catch (err) {
                    reject(err)
                }
            })
        },

        /**
         * @param commit
         * @param payload {SignInParams}
         * @return {Promise<SignInResult>}
         */
        getUserToken({ commit }, payload) {
            return new Promise((resolve, reject) => {
                $axiosUrl({
                    url: `${config.url}webservice/restful/token.php`,
                    method: 'POST',
                    data: {
                        username: payload.email,
                        password: payload.password,
                        service: config.service
                    }
                })
                    .then(resp => {
                        if (resp.data.errorcode) {
                            reject(resp.data)
                        }

                        const result = new SignInResult(resp.data)
                        commit('setUserToken', result)
                        resolve(result)
                    })
                    .catch(err => reject(err))
            })
        },
        reloadUserData({ dispatch }) {
            const cacheName = 'user-data'

            const cache = inMemoryCacheFactory(cacheName)
            if (cache.loading[cacheName]) {
                delete cache.loading[cacheName]
            }

            return dispatch('getUserData')
        },
        /**
         * @param commit
         * @param dispatch
         * @param state
         * @param payload {GetUserDataParams|Object}
         * @return {Promise<unknown>}
         */
        async getUserData({ commit, dispatch }, payload = {}) {
            const cacheName = 'user-data'

            const cache = inMemoryCacheFactory(cacheName)
            if (cache.loading[cacheName]) return cache.loading[cacheName]

            const { resolved, notfound } = cache.read([cacheName])

            if (notfound.length === 0) {
                const data = new GetUserDataResult(resolved[cacheName])
                commit('setUser', data)
                return Promise.resolve(data)
            }

            await dispatch('checkUserIsAdmin')

            return cache.loading[cacheName] = new Promise((resolve, reject) => {
                $axios({
                    method: apiList.user.getProfile.method,
                    params: {
                        wsfunction: apiList.user.getProfile.moodle_method
                    }
                })
                    .then(resp => {
                        //eslint-disable-next-line
                        if (!!resp.data?.errorcode) {
                            cache.stop(cacheName)
                            reject(resp.data)
                        } else {
                            // inMemoryCache.set(key, resp.data)
                            cache.save({ [cacheName]: resp.data })
                            const data = new GetUserDataResult(resp.data)
                            commit('setUser', data)
                            localStorage.setItem('user_email', data.email)
                            resolve(data)
                        }
                    })
                    .catch(err => {
                        cache.stop(cacheName)
                        reject(err)
                    })
            })
        },

        /**
         * @description Выход c удалением токена на бекэнде
         */
        logout({ commit, dispatch }) {
            const ret = $axios({
                method: apiList.user.deleteUserToken.method,
                params: {
                    wsfunction: apiList.user.deleteUserToken.moodle_method
                }
            })

            clearAuthStorage()
            localStorage.removeItem('user_email')
            commit('setUser', false)
            commit('setUserToken', false)

            return ret
        },

        /**
         * @description Удаление токена при выходе
         * @returns {Promise}
         */
        deleteTokenWhenUserLogout() {
            return new Promise((resolve, reject) => {
                $axios({
                    method: apiList.user.deleteUserToken.method,
                    params: {
                        wsfunction: apiList.user.deleteUserToken.moodle_method
                    }
                }).then(res => {
                    resolve(res)
                }).catch(err => {
                    reject(err)
                })
            })
        },

        /**
         *
         */
        restorePasswordRequest(_, payload) {
            return $axios({
                method: apiList.user.restorePasswordRequest.method,
                params: {
                    wsfunction: apiList.user.restorePasswordRequest.moodle_method,
                    email: payload.email,
                    forgot_url: `${window.location.origin}/auth/reset-password/@token@`
                }
            }).then(resp => resp.data)
        },
        /**
         *
         */
        setNewPassword(_, payload) {
            return $axios({
                method: apiList.auth.resetPassword.method,
                params: {
                    wsfunction: apiList.auth.resetPassword.moodle_method
                },
                data: {
                    token: payload.token,
                    newpassword: payload.pass1
                }
            }).then(resp => resp.data)
        },
        /**
         *
         */
        confirmUser(_, payload) {
            return $axios({
                method: apiList.auth.confirmUser.method,
                params: {
                    wsfunction: apiList.auth.confirmUser.moodle_method
                },
                data: {
                    username: payload.username,
                    secret: payload.secret
                }
            }).then(resp => resp.data)
        }
    },

    getters: {
        user: state => {
            return state.user
        },
        token: () => {
            return getValidToken()
        },
        isAdmin: state => {
            return state.isAdmin
        }
    }
}
